import { Component, h } from 'preact'
import axios from 'axios'
import Header from '../header'
import AlertComponent from '../ui-components/AlertComponent'
import Table from '../table/Table'
import LastActiveCell from './cells/LastActiveCell'
import UserCell from './cells/UserCell'
import RoleCell from './cells/RoleCell'
import ActionCell from './cells/ActionCell'
import WarningBox from '../ui-components/WarningBox'
import InviteUserModal from './InviteUserModal'
import InviteUserWarningModal from './InviteUserWarningModal'
import CancelInviteModal from './CancelInviteModal'
import Loader from '../ui-components/Loader'

type User = {
    id: number,
    username: string,
    email: string,
    role: string,
    lastActive: number,
    avatar: string,
    url: string
}

type Sort = {
    order: string,
    direction: string
}

export type Role = {
  id: number,
  name: string
}

interface Props {}

interface State {
    limit: number | 0,
    users: Array<User> | null,
    current: number | 0,
    roles: Array<Role> | null,
    messageType: string | null,
    message: string,
    sort: Sort,
    inviteeEmail: string | null,
    selectedRole: Role | null,
    inviteToCancel: number | null,
    showInviteUserModal: boolean,
    showInviteUserWarningModal: boolean,
    showCancelInvitationModal: boolean,
    isLoading: boolean,
    inviteErrors: {
      email: string | null
    }
}

class TeamOverview extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      limit: 0,
      users: [],
      current: 0,
      roles: [],
      messageType: null,
      message: '',
      sort: {
        order: 'lastActive',
        direction: 'desc'
      },
      inviteeEmail: null,
      selectedRole: null,
      inviteToCancel: null,
      showInviteUserModal: false,
      showInviteUserWarningModal: false,
      showCancelInvitationModal: false,
      isLoading: true,
      inviteErrors: {
        email: null
      }
    }

    this.getSeatsSettings = this.getSeatsSettings.bind(this)
    this.onInviteUser = this.onInviteUser.bind(this)
    this.onCancelInvitation = this.onCancelInvitation.bind(this)
    this.toggleInviteUserModal = this.toggleInviteUserModal.bind(this)
    this.toggleInviteUserWarningModal = this.toggleInviteUserWarningModal.bind(this)
    this.toggleCancelInvitationModal = this.toggleCancelInvitationModal.bind(this)
    this.resetAlert = this.resetAlert.bind(this)
  }

  async getSeatsSettings () {
    const response = await axios.get('/team/getSeatsSettings')
    const { limit, users, current, roles } = response.data
    this.setState({
      limit,
      users,
      current,
      roles,
      selectedRole: roles.filter((role: Role) => role.name === 'Community Manager')[0]
    })
  }

  async componentDidMount (): Promise<void> {
    try {
      await this.getSeatsSettings()
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Something went wrong' })
      throw e
    } finally {
      this.setState({ isLoading: false })
    }
  }

  async onInviteUser () {
    try {
      const { inviteeEmail, selectedRole } = this.state

      await axios.post('team/invite', {
        email: inviteeEmail,
        roleId: selectedRole?.id
      })

      this.setState({
        showInviteUserModal: false,
        messageType: 'success',
        message: 'Invitation sent successfully'
      }, () => this.getSeatsSettings())
    } catch (e) {
      const { status, data } = e.response
      if (status !== 500) {
        this.setState({ inviteErrors: { email: data } })
      } else {
        this.setState({ messageType: 'error', message: 'Something went wrong' })
      }
      throw e
    }
  }

  async onCancelInvitation () {
    try {
      const { inviteToCancel } = this.state

      await axios.post('/team/cancelInvite', {
        inviteId: inviteToCancel
      })

      this.setState({
        showCancelInvitationModal: false,
        messageType: 'success',
        message: 'Your invitation has been successfully cancelled'
      }, () => this.getSeatsSettings())
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Something went wrong' })
      throw e
    }
  }

  toggleInviteUserModal () {
    this.setState({
      showInviteUserModal: !this.state.showInviteUserModal,
      showInviteUserWarningModal: false,
      inviteeEmail: null,
      inviteErrors: { email: null }
    })
  }

  toggleCancelInvitationModal (inviteToCancel: number | null = null) {
    this.setState({
      inviteToCancel,
      showCancelInvitationModal: !this.state.showCancelInvitationModal
    })
  }

  toggleInviteUserWarningModal () {
    this.setState({
      showInviteUserWarningModal: !this.state.showInviteUserWarningModal
    })
  }

  getTableModel () {
    return [{
      header: 'Team member', accessor: 'team_member', cell: UserCell()
    }, {
      header: 'Role', accessor: 'role', cell: RoleCell()
    }, {
      header: 'Last active', accessor: 'last_active', cell: LastActiveCell()
    }, {
      header: 'Action', accessor: 'action', cell: ActionCell(this.toggleCancelInvitationModal)
    }]
  }

  resetAlert () {
    this.setState({
      messageType: null,
      message: ''
    })
  }

  render (_: Props, {
    isLoading,
    limit,
    users,
    current,
    roles,
    messageType,
    message,
    sort,
    selectedRole,
    inviteeEmail,
    showInviteUserModal,
    showInviteUserWarningModal,
    showCancelInvitationModal,
    inviteErrors
  }: State) {
    if (isLoading) return <Loader />

    const isUnlimited = limit === 0
    const limitExceeded = current > limit
    const limitEqualToCurrent = current === limit

    return (
      <div className='team-overview-page'>
        <Header title='Team Overview' disableCancelButton disableSaveButton customButtons={[{
          variant: 'primary',
          title: 'Invite member',
          action: (limitEqualToCurrent || limitExceeded) && !isUnlimited ? this.toggleInviteUserWarningModal : this.toggleInviteUserModal
        }]} />
        {messageType && <AlertComponent messageType={messageType} message={message} sticky isVisibleFor={5000} onTimeoutEnd={this.resetAlert} onClose={this.resetAlert} />}
        {!isUnlimited && limitExceeded && <WarningBox
          message="🎉 Great to see that your team is growing! You've gone over your current seat limit
          - don’t worry, we won’t charge you anything without your consent."
        />}
        <div className='admin-seats-usage'>
          <div className='number-of-members'>
            { current } {current === 1 ? 'Member' : 'Members'} <a className='explanation' target='__blank' href='https://community.insided.com/article/840?t=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhcnRpY2xlX2lkIjo4NDB9.1mttHH1LN18perj3A7GFQyA3GMyAZeR5Zx65CYOJF5Y'>
            (Who counts as a team member?)
            </a>
          </div>
          {!isUnlimited && <div className='number-of-seats'> { current } out of { limit } seats assigned </div>}
        </div>
        <div className='team-overview'>
          <Table model={this.getTableModel()} rows={users} rowId='id' sort={sort} />
        </div>
        <InviteUserModal
          isOpen={showInviteUserModal}
          roles={roles}
          inviteeEmail={inviteeEmail}
          selectedRole={selectedRole}
          onEmailChange={(inviteeEmail: string) => this.setState({ inviteeEmail })}
          onRoleChange={(selectedRole: Role) => this.setState({ selectedRole })}
          onInviteUser={this.onInviteUser}
          onClose={this.toggleInviteUserModal}
          errors={inviteErrors}
        />
        <InviteUserWarningModal
          limitEqualToCurrent={limitEqualToCurrent}
          isOpen={showInviteUserWarningModal}
          onContinueInviting={this.toggleInviteUserModal}
          onClose={this.toggleInviteUserWarningModal}
        />
        <CancelInviteModal isOpen={showCancelInvitationModal} onCancelInvitation={this.onCancelInvitation} onClose={this.toggleCancelInvitationModal} />
      </div>
    )
  }
}

export default TeamOverview
